import type { HTMLAttributes, ReactNode } from 'react';
import { cn } from '../lib/cn.js';

/**
 * Badge — a small status label.
 *
 * One component covers every tinted pill in both references: the "Recommendation"
 * and "Reply Needed" chips, the "reversed" / "reversal" markers on a journal
 * row, "9 checks passing", "group" vs "postable" in the chart of accounts.
 * They differ only by intent, which is why there is no second component.
 *
 * ── ON THE `dot` VARIANT ──────────────────────────────────────────────────
 *
 * Intent is carried by background colour, and roughly one in twelve men cannot
 * separate the green and amber fills reliably. Where a badge is the ONLY signal
 * — a status column with no other cue — pass `dot` so the state also has a
 * shape, or better, keep the word ("passing" / "failing") rather than reducing
 * it to a colour.
 */

export type BadgeIntent = 'neutral' | 'info' | 'success' | 'warning' | 'danger';

const INTENT: Record<BadgeIntent, string> = {
  neutral: 'bg-neutral-bg text-neutral-fg border-neutral-border',
  info: 'bg-info-bg text-info-fg border-info-border',
  success: 'bg-success-bg text-success-fg border-success-border',
  warning: 'bg-warning-bg text-warning-fg border-warning-border',
  danger: 'bg-danger-bg text-danger-fg border-danger-border',
};

const DOT: Record<BadgeIntent, string> = {
  neutral: 'bg-neutral-fg',
  info: 'bg-info-fg',
  success: 'bg-success-fg',
  warning: 'bg-warning-fg',
  danger: 'bg-danger-fg',
};

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  intent?: BadgeIntent;
  /** Adds a filled dot before the label, so state is not colour alone. */
  dot?: boolean;
  /** An icon before the label. Mutually exclusive with `dot` in practice. */
  leading?: ReactNode;
  /** Drops the border for use inside an already-bordered container. */
  subtle?: boolean;
}

export function Badge({
  intent = 'neutral',
  dot = false,
  subtle = false,
  leading,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center gap-(--badge-gap) align-middle',
        'h-(--badge-height) px-(--badge-padding-x) rounded-(--badge-radius)',
        'text-(length:--badge-font-size) font-medium leading-none whitespace-nowrap',
        !subtle && 'border',
        INTENT[intent],
        className,
      )}
      {...rest}
    >
      {dot && <span className={cn('size-1.5 rounded-full shrink-0', DOT[intent])} aria-hidden />}
      {leading}
      {children}
    </span>
  );
}
